package org.example.project.dashboard

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.ScreenEnvironmentApi
import org.example.project.service.GlobalManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element
import org.w3c.dom.events.Event
import kotlin.math.ceil

private const val ENVIRONMENT_ROW_GRID = "170px 60px 60px"
private const val ENVIRONMENT_MIN_COL_W = 350
private const val ENVIRONMENT_BLOCK_GAP = 32

@Composable
fun DashboardScreenUmgebungen() {
    Style(cssRules = SharedWorkspaceStyle.cssRules)
    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    val inventory = remember {
        ScreenManager.allIndexKeys()
            .filter { DashboardNode.SYS in ScreenManager.codeNodesForKey(it) }
            .sortedBy { ScreenManager.labelForKey(it).lowercase() }
            .map { key -> All(screenKey = key, label = ScreenManager.labelForKey(key)) }
    }
    var settings by remember { mutableStateOf<Map<String, All>>(emptyMap()) }
    var search by remember { mutableStateOf("") }
    var numCols by remember { mutableStateOf(1) }
    var containerEl by remember { mutableStateOf<Element?>(null) }

    fun measure() {
        val width = containerEl?.getBoundingClientRect()?.width ?: return
        if (width <= 0) return
        numCols = ((width + ENVIRONMENT_BLOCK_GAP) / (ENVIRONMENT_MIN_COL_W + ENVIRONMENT_BLOCK_GAP)).toInt().coerceIn(1, 8)
    }

    suspend fun reload() {
        settings = ScreenEnvironmentApi.all().associateBy { it.screenKey }
    }

    fun setting(key: String): All = settings[key] ?: All(screenKey = key)

    fun change(key: String, environment: String, enabled: Boolean) {
        val current = setting(key)
        val changed = when (environment) {
            "dev" -> current.copy(dev = enabled)
            else -> current.copy(live = enabled)
        }
        settings = settings + (key to changed)
    }

    LaunchedEffect(Unit) { reload() }

    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = { measure() }
        window.addEventListener("resize", listener)
        onDispose { window.removeEventListener("resize", listener) }
    }

    val visible = inventory.filter {
        search.isBlank() || it.label.contains(search, true) || it.screenKey.contains(search, true)
    }
    val columns = if (visible.isEmpty() || numCols == 0) listOf(emptyList())
    else visible.chunked(ceil(visible.size / numCols.toDouble()).toInt())

    Div({ classes(SharedWorkspaceStyle.workspace) }) {
        Div({ classes(SharedWorkspaceStyle.side, SharedWorkspaceStyle.content); style { width(100.percent) } }) {
            Div({
                classes(SharedWorkspaceStyle.contentInner)
                style {
                    maxWidth(100.percent)
                    overflowY("auto")
                    flex(1)
                    minHeight(0.px)
                }
            }) {
                if (internalTitleVisible) {
                    Div({ classes(SharedWorkspaceStyle.rightTitle) }) {
                        Span({ classes(SharedWorkspaceStyle.sectionTitle) }) { Text("Screen-Umgebungen") }
                    }
                }
                Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center); gap(22.px); flexWrap(FlexWrap.Wrap) } }) {
                    Input(InputType.Text) {
                        classes(SharedWorkspaceStyle.textInput)
                        value(search)
                        placeholder("Screen suchen...")
                        onInput { search = it.value }
                        style { width(220.px) }
                    }
                    LumaticaButton(
                        label = "Speichern",
                        onClick = {
                            scope.launch {
                                val saved = ScreenEnvironmentApi.save(inventory.map { setting(it.screenKey) })
                                settings = saved.associateBy { it.screenKey }
                                ScreenManager.applyScreenEnvironments(saved, ScreenEnvironmentApi.environment())
                                GlobalManager.triggerSessionConfigReload()
                            }
                        },
                        variant = LumaticaButtonVariant.Action,
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Refresh",
                        onClick = { scope.launch { reload() } },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 42
                    )
                }
                Div({
                    style {
                        alignSelf("center"); width(600.px); maxWidth(100.percent); height(1.px)
                        backgroundColor(rgba(255, 255, 255, 0.14)); marginTop(40.px); marginBottom(40.px)
                    }
                })
                Div({
                    style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); width(100.percent); paddingBottom(40.px) }
                    ref { element ->
                        containerEl = element
                        window.requestAnimationFrame { measure() }
                        onDispose { containerEl = null }
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Grid); gridTemplateColumns("repeat($numCols, ${ENVIRONMENT_MIN_COL_W}px)")
                            gap(ENVIRONMENT_BLOCK_GAP.px); justifyContent(JustifyContent.Center); boxSizing("border-box")
                        }
                    }) {
                        columns.forEachIndexed { index, columnEntries ->
                            Div({
                                style {
                                    display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); minWidth(0.px)
                                    if (index < columns.lastIndex) {
                                        property("border-right", "1px solid rgba(255,255,255,0.07)")
                                        paddingRight(20.px)
                                    }
                                }
                            }) {
                                Div({ classes(SharedWorkspaceStyle.permissionHeader); style { gridTemplateColumns(ENVIRONMENT_ROW_GRID) } }) {
                                    Span { Text("Screenname") }
                                    Span { Text("Dev") }
                                    Span { Text("Live") }
                                }
                                columnEntries.forEach { entry ->
                                    val current = setting(entry.screenKey)
                                    Div({ classes(SharedWorkspaceStyle.permissionRow); style { gridTemplateColumns(ENVIRONMENT_ROW_GRID) } }) {
                                        Span({ classes(SharedWorkspaceStyle.permissionName) }) { Text(entry.label) }
                                        LumaticaCheckbox(current.dev, { change(entry.screenKey, "dev", it) })
                                        LumaticaCheckbox(current.live, { change(entry.screenKey, "live", it) })
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
